import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { onUnauthorized } from '../../api/client';
import { getSession, sessionStore } from '../../api/session';
import { clearUserCaches, initPwa } from '../../lib/pwa';
import { invalidate } from '../../lib/query';
import { useStore } from '../../lib/store';
import { player } from '../../player/engine';
import { settingsStore } from '../../settings';
import { toast } from '../primitives';
import { applyTheme } from '../theme/themeMode';
import { reportError } from './errorReporter';
import { HOME_LOCATION } from './navigator';
import { openAppUrl } from './nav';
import { returnOnReactivation } from './reactivation';

/** App-wide side effects that do not depend on being signed in. */
export function AppEffects() {
  const navigate = useNavigate();
  const theme = useStore(settingsStore, (s) => s.theme);
  const themeApplied = useRef(false);

  // main.tsx applies the stored theme before the first render; later changes cross-fade (AnimatedTheme, 200 ms).
  useEffect(() => {
    applyTheme(theme, { animate: themeApplied.current });
    themeApplied.current = true;
  }, [theme]);

  // Service-worker notification clicks navigate in-app.
  useEffect(() => initPwa(openAppUrl), []);

  // Home-screen shortcut "Continue listening" (/?resume=1).
  useEffect(() => {
    const { pathname, search } = window.location;
    if (pathname !== '/' || new URLSearchParams(search).get('resume') !== '1') return;
    if (player.state.itemId) navigate('/player', { replace: true, state: { background: HOME_LOCATION } });
    else navigate('/', { replace: true });
  }, [navigate]);

  // A connection switched back on from the "No active connections" gate: back to the library with it.
  useEffect(() => returnOnReactivation(), []);

  // A session the server rejected and could not refresh: ask for credentials again.
  useEffect(
    () =>
      onUnauthorized(() => {
        if (!getSession()) return;
        toast.error('Your session expired. Please sign in again.');
        player.stop().catch((error: unknown) => reportError(error, 'player.stop', 'session'));
        sessionStore.set(null);
        invalidate();
        clearUserCaches().catch((error: unknown) => reportError(error, 'clearUserCaches', 'session'));
      }),
    [],
  );

  return null;
}
